import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  LayoutDashboard, Users, CalendarCheck, IndianRupee, BookOpen,
  Award, Briefcase, Building2, Settings, ClipboardList, Languages,
  MessageSquare, UserCog, LogOut, ChevronRight, ChevronLeft, GraduationCap,
  FlaskConical, Activity, Star, Wallet, Receipt, Hammer, Megaphone, AlertCircle, Package
} from 'lucide-react';
import NotificationBell from '../ui/NotificationBell';
import Chat from '../../pages/Chat';

const navItems = [
  { section: 'Main', items: [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', pkey: 'dashboard' },
  ]},
  { section: 'Students', items: [
    { path: '/students',    icon: Users,         label: 'Student Profile',   pkey: 'students' },
    { path: '/attendance',  icon: CalendarCheck, label: 'Attendance',        pkey: 'attendance' },
    { path: '/fees',        icon: IndianRupee,   label: 'Fees',              pkey: 'fees' },
    { path: '/fee-schedules', icon: IndianRupee,   label: 'Fee Schedule',      pkey: 'fee_schedules' },
    { path: '/assignments', icon: ClipboardList, label: 'Assignments',       pkey: 'assignments' },
    { path: '/language',    icon: Languages,     label: 'Language Training', pkey: 'language_training' },
    { path: '/requests',    icon: MessageSquare, label: 'Student Requests',  pkey: 'requests' },
  ]},
  { section: 'Academic', items: [
    { path: '/exams',        icon: BookOpen,     label: 'Examinations', pkey: 'exams' },
    { path: '/certificates', icon: Award,        label: 'Certificates', pkey: 'certificates' },
    { path: '/internship',   icon: FlaskConical, label: 'Internship',   pkey: 'internship' },
  ]},
  { section: 'Operations', items: [
    { path: '/hostel',    icon: Building2, label: 'Hostel',         pkey: 'hostel' },
    { path: '/placement', icon: Briefcase, label: 'Placement Cell', pkey: 'placement' },
  ]},
  { section: 'Admin', items: [
    { path: '/master',   icon: Settings,  label: 'Master Data',          pkey: 'master_data' },
    { path: '/staff',    icon: UserCog,   label: 'Staff & Users',        pkey: 'staff_admin' },
    { path: '/applications', icon: ClipboardList, label: 'Applications', pkey: 'applications' },
    { path: '/hr',       icon: Users,     label: 'ARMSTECH HR',          pkey: 'hr' },
    { path: '/nactet',   icon: Star,      label: 'NACTET Scholarship',   pkey: 'nactet' },
    { path: '/accounts', icon: Wallet,    label: 'Accounts',             pkey: 'accounts' },
    { path: '/gst',      icon: Receipt,   label: 'GST Purchase & Sales', pkey: 'gst' },
    { path: '/civil',    icon: Hammer,    label: 'Civil Construction',   pkey: 'civil' },
    { path: '/crm',      icon: Megaphone, label: 'Marketing CRM',        pkey: 'crm' },
    { path: '/assets',   icon: Package,   label: 'Assets & Consumables', pkey: 'assets' },
    { path: '/activity', icon: Activity,   label: 'Activity Log',          pkey: 'staff_admin' },
    { path: '/concerns', icon: AlertCircle, label: 'Concerns & Requests',   pkey: 'concerns' },
    { path: '/expenses', icon: Receipt, label: 'My Expenses', pkey: 'expenses', alwaysShow: false },
  ]},
];

export default function Layout() {
  const { staff, signOut, isEditor, canView } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // desktop only: icon-only sidebar, remembered per browser
  const [collapsed, setCollapsed] = useState(() => { try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch (e) { return false; } });
  const toggleCollapsed = () => setCollapsed(c => { const n = !c; try { localStorage.setItem('sidebarCollapsed', n ? '1' : '0'); } catch (e) {} return n; });

  const initials = staff?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  // Prevent body scroll when sidebar open on mobile
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const goTo = (path) => { navigate(path); setOpen(false); };

  return (
    <div className="app-shell">

      {/* ── MOBILE TOP BAR ─────────────────────────────────── */}
      <div className="mobile-bar" style={{paddingTop:'60px'}}>
        <button
          onClick={() => setOpen(true)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '6px 8px', borderRadius: 8, fontSize: 0,
            display: 'flex', alignItems: 'center',
          }}
          aria-label="Open menu"
        >
          {/* Hamburger icon drawn with divs - no icon library needed */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, width: 22 }}>
            <div style={{ height: 2, background: 'var(--text)', borderRadius: 2 }}/>
            <div style={{ height: 2, background: 'var(--text)', borderRadius: 2 }}/>
            <div style={{ height: 2, background: 'var(--text)', borderRadius: 2 }}/>
          </div>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 26, height: 26, background: 'var(--accent)', borderRadius: 6,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <GraduationCap size={14} color="#fff" />
          </div>
          <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--text)' }}>
            ARMSTECH ERP
          </span>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <NotificationBell staffId={staff?.id} />
          <div className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>
            {initials}
          </div>
        </div>
      </div>

      {/* ── BACKDROP ───────────────────────────────────────── */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            zIndex: 199,
            WebkitTapHighlightColor: 'transparent',
          }}
        />
      )}

      {/* ── SIDEBAR ────────────────────────────────────────── */}
      <aside className={`sidebar${open ? ' sidebar-mobile-open' : ''}${collapsed ? ' collapsed' : ''}`}>

        <div className="sidebar-logo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{
              width: 32, height: 32, background: 'var(--accent)', borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <GraduationCap size={18} color="#fff" />
            </div>
            <div style={{ flex: 1 }}>
              <h1>ARMSTECH ERP</h1>
              <span>Civil & Mechanical</span>
            </div>
            {!open && <span className="sidebar-bell"><NotificationBell staffId={staff?.id} /></span>}
            <button className="sidebar-collapse-btn" onClick={toggleCollapsed} title={collapsed ? 'Expand menu' : 'Collapse menu'} aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}>
              {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
            </button>
            {/* Close button — only shown when sidebar is open (mobile) */}
            {open && (
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)', border: 'none',
                  borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                  color: '#fff', fontSize: 18, lineHeight: 1,
                  flexShrink: 0,
                }}
              >✕</button>
            )}
          </div>
        </div>

        {navItems.map(({ section, items }) => {
          const visible = items.filter(i => i.alwaysShow || canView(i.pkey));
          if (!visible.length) return null;
          return (
            <div className="sidebar-section" key={section}>
              <div className="sidebar-section-label">{section}</div>
              {visible.map(({ path, icon: Icon, label }) => {
                const active = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
                return (
                  <div
                    key={path}
                    className={`sidebar-item ${active ? 'active' : ''}`}
                    onClick={() => goTo(path)}
                    title={collapsed ? label : undefined}
                  >
                    <Icon />
                    <span style={{ flex: 1 }}>{label}</span>
                    {active && <ChevronRight size={12} className="sidebar-chev" style={{ opacity: 0.6 }} />}
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className="sidebar-user">
          <div className="avatar">{initials}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{staff?.full_name || 'User'}</div>
            <div className="sidebar-user-role">
              {isEditor ? 'Editor' : 'Commentator'} · {staff?.staff_category?.replace('_', ' ') || ''}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={signOut} title="Sign Out">
            <LogOut size={14} />
          </button>
        </div>
      </aside>

      {/* ── MAIN CONTENT ───────────────────────────────────── */}
      <div className="main-content">
        <Outlet />
      </div>

      {staff?.id && <Chat />}
    </div>
  );
}
